import { useState } from "react";
import { Heart, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { toast } from "sonner";

/**
 * Wishlist toggle button — heart icon.
 * Variants: "icon" (small overlay button on cards) | "full" (button on product detail).
 */
export default function WishlistButton({ productId, variant = "icon", onRequireAuth }) {
  var auth = useAuth();
  var wishlist = useWishlist();
  var [busy, setBusy] = useState(false);

  var inWishlist = wishlist.isInWishlist(productId);

  function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;

    if (!auth.isAuthenticated) {
      toast.info("Sign in to save items to your wishlist");
      if (onRequireAuth) onRequireAuth();
      return;
    }

    setBusy(true);
    var action = inWishlist ? wishlist.removeFromWishlist(productId) : wishlist.addToWishlist(productId);
    action.then(function(res) {
      if (inWishlist) {
        toast.success("Removed from wishlist");
      } else {
        if (res && res.auto_subscribed_to_notifications) {
          toast.success("Added to wishlist — we'll email you when it's back in stock");
        } else {
          toast.success("Added to wishlist");
        }
      }
    }).catch(function() {
      toast.error("Something went wrong. Try again.");
    }).finally(function() {
      setBusy(false);
    });
  }

  if (variant === "full") {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        data-testid={"wishlist-toggle-" + productId}
        className={
          "inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm font-heading uppercase tracking-wider text-sm border transition-all " +
          (inWishlist
            ? "bg-[#C0A062]/10 border-[#C0A062] text-[#C0A062] hover:bg-[#C0A062]/20"
            : "bg-transparent border-white/10 text-white hover:border-[#C0A062]/50 hover:text-[#C0A062]")
        }
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Heart className={"w-4 h-4 " + (inWishlist ? "fill-[#C0A062]" : "")} />
        )}
        <span>{inWishlist ? "In Wishlist" : "Add to Wishlist"}</span>
      </button>
    );
  }

  // Icon variant — designed for product card overlay
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      data-testid={"wishlist-toggle-" + productId}
      aria-label={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
      className={
        "w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md transition-all " +
        (inWishlist
          ? "bg-[#C0A062] text-black hover:bg-[#d4b675]"
          : "bg-black/60 text-white hover:bg-black/80 hover:text-[#C0A062]")
      }
    >
      {busy ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Heart className={"w-4 h-4 " + (inWishlist ? "fill-current" : "")} />
      )}
    </button>
  );
}
